import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback
} from 'react';

type LoadingContextType = {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  withLoading: <T>(promise: Promise<T>) => Promise<T>;
};

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export function ReportsLoadingProvider({ children }: { children: ReactNode }) {
  const [isLoading, setLoading] = useState(false);

  const withLoading = useCallback(
    async <T,>(promise: Promise<T>): Promise<T> => {
      setLoading(true);
      try {
        return await promise;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      isLoading,
      setLoading,
      withLoading
    }),
    [isLoading, setLoading, withLoading]
  );

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
}

export function useReportsLoading() {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error(
      'useReportsLoading must be used within a ReportsLoadingProvider'
    );
  }
  return context;
}
